import { tutorialTaskDone } from '../../../../shared/src/tutorial/events'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Users, Pencil, Trash2, MessageSquare } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button, Modal, Input, EmptyState, Avatar, Badge } from '@/components/ui'
import { useAuthStore } from '@/store/authStore'
import { athletesApi, type DesktopAthlete, type AthleteUpdate } from '@/api/athletesApi'
import { AthleteWorkbench } from './AthleteWorkbench'
import { groupsApi } from '@/api/groupsApi'
import { athleteNotesApi, NOTE_TYPE_CONFIG } from '@/api/athleteNotesApi'
import type { AthleteNote, NoteType, CreateAthleteNoteRequest } from '@/api/athleteNotesApi'
import { toast } from '@/components/ui/Toast'
import type { CreateAthleteRequest } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import { ru } from 'date-fns/locale'

function formatBirthDate(isoDate: string): string {
  const date = new Date(isoDate)
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()

  const today = new Date()
  let age = today.getFullYear() - yyyy
  const monthDiff = today.getMonth() - date.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
    age--
  }

  return `${dd}.${mm}.${yyyy} (${age} лет)`
}

export const AthletesPage: React.FC = () => {
  const scope = useAuthStore(s => `${s.user?.id || 'session'}:${s.user?.selected_organization_id || ''}:${s.currentAssistantId || ''}`)
  return <AthletesPageContent key={scope} scope={scope} />
}
const AthletesPageContent: React.FC<{ scope: string }> = ({ scope }) => {
  const [params] = useSearchParams()
  const linkedAthleteId = params.get('athlete')
  const openedLink = useRef<string | null>(null)
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId)
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<CreateAthleteRequest>({ name: '' })
  const [selectedAthlete, setSelectedAthlete] = useState<DesktopAthlete | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState<AthleteUpdate>({})

  const [sportKey, setSportKey] = useState('')
  const [sportVal, setSportVal] = useState('')
  const [detailTab, setDetailTab] = useState<'data' | 'notes'>('data')
  const [showAddNote, setShowAddNote] = useState(false)
  const [noteForm, setNoteForm] = useState<{ title: string; content: string; note_type: NoteType }>({ title: '', content: '', note_type: 'general' })

  const openDetail = useCallback((athlete: DesktopAthlete) => {
    setSelectedAthlete(athlete)
    setEditMode(false)
    setEditForm({
      name: athlete.name,
      date_of_birth: athlete.date_of_birth ? athlete.date_of_birth.split('T')[0] : undefined,
      gender: athlete.gender,
      phone: athlete.phone,
      email: athlete.email,
      parent_phone: athlete.parent_phone,
      parent_name: athlete.parent_name,
      parent_email: athlete.parent_email,
      address: athlete.address,
      notes: athlete.notes,
      weight: athlete.weight,
      height: athlete.height,
      medical_clearance: athlete.medical_clearance ? athlete.medical_clearance.split('T')[0] : undefined,
      sport_data: athlete.sport_data ? { ...athlete.sport_data } : {},
      paused: !!athlete.paused_at,
    })
    setSportKey('')
    setSportVal('')
    setDetailTab('data')
    setShowAddNote(false)
  }, [])

  const closeDetail = () => {
    if (editMode && !window.confirm('Закрыть карточку и отменить несохранённые правки?')) return
    setSelectedAthlete(null)
    setEditMode(false)
  }

  const { data: athletes = [], isLoading, isSuccess: athletesReady, isError: athletesError, refetch: refetchAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  })

  useEffect(() => {
    if (!linkedAthleteId || !athletesReady || openedLink.current === linkedAthleteId) return
    const athlete = athletes.find(row => row.id === linkedAthleteId)
    if (athlete) { openDetail(athlete); openedLink.current = linkedAthleteId }
  }, [linkedAthleteId, athletesReady, athletes, openDetail])

  const { data: groups = [], isSuccess: groupsReady, isError: groupsError } = useQuery({
    queryKey: ['groups', currentAssistantId],
    queryFn: () => groupsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  })

  const getGroupName = (groupId?: string) => {
    if (!groupId) return '-'
    const group = groups.find(g => g.id === groupId)
    return group?.name || '-'
  }

  const createMutation = useMutation({
    mutationFn: (req: CreateAthleteRequest) => athletesApi.create(currentAssistantId!, req),
    onSuccess: () => {
      tutorialTaskDone('athlete')
      queryClient.invalidateQueries({ queryKey: ['athletes'] })
      setShowCreate(false)
      setForm({ name: '' })
      toast.success('Спортсмен добавлен')
    },
    onError: (err: any) => toast.error(err?.error || 'Ошибка создания'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: AthleteUpdate }) =>
      athletesApi.update(currentAssistantId!, id, req),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['athletes'] })
      setSelectedAthlete(updated)
      setEditMode(false)
      toast.success('Спортсмен обновлён')
    },
    onError: (err: any) => toast.error(err?.error || 'Ошибка обновления'),
  })

  // Notes queries
  const { data: notesData } = useQuery({
    queryKey: ['athlete-notes', selectedAthlete?.id],
    queryFn: () => athleteNotesApi.getByAthleteId(selectedAthlete!.id, { limit: 50 }),
    enabled: !!selectedAthlete && detailTab === 'notes',
  })

  const createNoteMutation = useMutation({
    mutationFn: (req: CreateAthleteNoteRequest) => athleteNotesApi.create(selectedAthlete!.id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athlete-notes', selectedAthlete?.id] })
      setShowAddNote(false)
      setNoteForm({ title: '', content: '', note_type: 'general' })
      toast.success('Заметка добавлена')
    },
    onError: (err: any) => toast.error(err?.error || 'Ошибка'),
  })

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => athleteNotesApi.delete(selectedAthlete!.id, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athlete-notes', selectedAthlete?.id] })
      toast.success('Заметка удалена')
    },
  })

  if (!currentAssistantId) {
    return (
      <>
        <Header title="Спортсмены" />
        <EmptyState icon={Users} title="Выберите направление" description="Сначала создайте или выберите направление" />
      </>
    )
  }

  return (
    <>
      <Header
        title="Спортсмены"
        subtitle={`${athletes.length} спортсменов`}
        actions={
          <div className="flex gap-2">
            <Button data-tutorial="new-athlete" size="sm" onClick={() => setShowCreate(true)}><Plus size={16} className="mr-1.5" />Добавить</Button>
          </div>
        }
      />

      <div className="pc-page">
        {linkedAthleteId && athletesReady && !athletes.some(row => row.id === linkedAthleteId) && <p className="pc-panel" role="status">Этот спортсмен недоступен в выбранном направлении</p>}
        {isLoading ? <p role="status" className="pc-muted">Загружаем спортсменов...</p> : athletesError ? <div className="pc-panel p-8" role="alert">Не удалось загрузить спортсменов. <Button variant="secondary" onClick={() => void refetchAthletes()}>Повторить</Button></div> : <AthleteWorkbench athletes={athletes} groups={groups} assistantId={currentAssistantId} scope={scope} groupsReady={groupsReady} onOpen={openDetail} />}
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Новый спортсмен">
        <form data-tutorial="athlete-form"
          onSubmit={(e) => {
            e.preventDefault()
            createMutation.mutate(form)
          }}
          className="flex flex-col gap-4"
        >
          <Input label="Имя *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          <Input label="Дата рождения" type="date" value={form.date_of_birth || ''} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value || undefined })} />
          <Input label="Телефон" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value || undefined })} />
          <Input label="Email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value || undefined })} />
          <Input label="Телефон родителя" value={form.parent_phone || ''} onChange={(e) => setForm({ ...form, parent_phone: e.target.value || undefined })} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="secondary" type="button" onClick={() => setShowCreate(false)}>Отмена</Button>
            <Button type="submit" loading={createMutation.isPending}>Создать</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!selectedAthlete} onClose={closeDetail} title={editMode ? 'Редактирование' : 'Спортсмен'} width="xl">
        {selectedAthlete && !editMode && (
          <div className="flex flex-col gap-5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar name={selectedAthlete.name} size={56} />
                <div>
                  <div className="text-[18px] font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedAthlete.name}</div>
                  {selectedAthlete.date_of_birth && (
                    <div className="text-[14px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                      {formatBirthDate(selectedAthlete.date_of_birth)}
                    </div>
                  )}
                </div>
              </div>
              <Badge variant={selectedAthlete.paused_at ? 'warning' : 'success'}>
                {selectedAthlete.paused_at ? 'На паузе' : 'Занимается'}
              </Badge>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <button
                className="flex-1 py-1.5 px-3 rounded-md text-[13px] font-medium transition-all"
                style={{
                  backgroundColor: detailTab === 'data' ? 'var(--bg-primary)' : 'transparent',
                  color: detailTab === 'data' ? 'var(--text-primary)' : 'var(--text-hint)',
                  boxShadow: detailTab === 'data' ? 'var(--card-shadow)' : 'none',
                }}
                onClick={() => setDetailTab('data')}
              >
                Данные
              </button>
              <button
                className="flex-1 py-1.5 px-3 rounded-md text-[13px] font-medium transition-all flex items-center justify-center gap-1.5"
                style={{
                  backgroundColor: detailTab === 'notes' ? 'var(--bg-primary)' : 'transparent',
                  color: detailTab === 'notes' ? 'var(--text-primary)' : 'var(--text-hint)',
                  boxShadow: detailTab === 'notes' ? 'var(--card-shadow)' : 'none',
                }}
                onClick={() => setDetailTab('notes')}
              >
                <MessageSquare size={14} />
                Заметки
                {notesData?.total ? <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent)' }}>{notesData.total}</span> : null}
              </button>
            </div>

            {/* Tab: Data */}
            {detailTab === 'data' && (
              <>
            <SectionTitle>Основные данные</SectionTitle>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <DetailRow label="Телефон" value={selectedAthlete.phone} />
              <DetailRow label="Email" value={selectedAthlete.email} />
              <DetailRow label="Группа" value={getGroupName(selectedAthlete.group_id)} />
              <DetailRow label="Пол" value={selectedAthlete.gender === 'male' ? 'Мужской' : selectedAthlete.gender === 'female' ? 'Женский' : selectedAthlete.gender} />
              <DetailRow label="Вес" value={selectedAthlete.weight ? `${selectedAthlete.weight} кг` : undefined} />
              <DetailRow label="Рост" value={selectedAthlete.height ? `${selectedAthlete.height} см` : undefined} />
              <DetailRow label="Мед. допуск до" value={selectedAthlete.medical_clearance ? new Date(selectedAthlete.medical_clearance).toLocaleDateString('ru-RU') : undefined} />
              <DetailRow label="Адрес" value={selectedAthlete.address} />
            </div>

            {/* Контакты родителей */}
            <SectionTitle>Контакты родителей</SectionTitle>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <DetailRow label="Телефон родителя" value={selectedAthlete.parent_phone} />
              <DetailRow label="Имя родителя" value={selectedAthlete.parent_name} />
              <DetailRow label="Email родителя" value={selectedAthlete.parent_email} />
            </div>

            {/* Спортивные данные */}
            {selectedAthlete.sport_data && Object.keys(selectedAthlete.sport_data).length > 0 && (
              <>
                <SectionTitle>Спортивные данные</SectionTitle>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {Object.entries(selectedAthlete.sport_data).map(([key, val]) => (
                    <DetailRow key={key} label={key} value={String(val)} />
                  ))}
                </div>
              </>
            )}

            {/* Заметки */}
            {selectedAthlete.notes && (
              <>
                <SectionTitle>Заметки</SectionTitle>
                <div className="text-[14px] whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{selectedAthlete.notes}</div>
              </>
            )}

            {/* Метаданные */}
            <div className="flex gap-6 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              <div className="text-[12px]" style={{ color: 'var(--text-hint)' }}>
                Создан: {new Date(selectedAthlete.created_at).toLocaleDateString('ru-RU')}
              </div>
              <div className="text-[12px]" style={{ color: 'var(--text-hint)' }}>
                Обновлён: {new Date(selectedAthlete.updated_at).toLocaleDateString('ru-RU')}
              </div>
            </div>

              </>
            )}

            {/* Tab: Notes */}
            {detailTab === 'notes' && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {notesData?.total ? `${notesData.total} заметок` : ''}
                  </span>
                  <Button size="sm" onClick={() => setShowAddNote(!showAddNote)}>
                    <Plus size={14} className="mr-1" />Добавить
                  </Button>
                </div>

                {/* Add note form */}
                {showAddNote && (
                  <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <div className="grid grid-cols-2 gap-3">
                      <Input label="Заголовок *" value={noteForm.title} onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })} placeholder="Краткий заголовок" />
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>Тип</label>
                        <select
                          value={noteForm.note_type}
                          onChange={(e) => setNoteForm({ ...noteForm, note_type: e.target.value as NoteType })}
                          className="h-10 rounded-lg px-3 text-[14px] outline-none"
                          style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                        >
                          {Object.entries(NOTE_TYPE_CONFIG).map(([key, cfg]) => (
                            <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <textarea
                      value={noteForm.content}
                      onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                      placeholder="Текст заметки..."
                      rows={3}
                      className="w-full rounded-lg px-3 py-2 text-[14px] outline-none resize-none"
                      style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setShowAddNote(false)}>Отмена</Button>
                      <Button
                        size="sm"
                        loading={createNoteMutation.isPending}
                        onClick={() => {
                          if (!noteForm.title.trim() || !noteForm.content.trim()) { toast.error('Заполните заголовок и текст'); return }
                          createNoteMutation.mutate({ ...noteForm, assistant_id: currentAssistantId! })
                        }}
                      >
                        Сохранить
                      </Button>
                    </div>
                  </div>
                )}

                {/* Notes list */}
                {notesData?.notes && notesData.notes.length > 0 ? (
                  <div className="space-y-2">
                    {notesData.notes.map((note: AthleteNote) => {
                      const cfg = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.general
                      return (
                        <div
                          key={note.id}
                          className="rounded-xl p-4"
                          style={{ backgroundColor: 'var(--bg-secondary)' }}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span>{cfg.icon}</span>
                              <span className="text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>{note.title}</span>
                              <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${cfg.color}18`, color: cfg.color }}>{cfg.label}</span>
                            </div>
                            <button
                              onClick={() => { if (confirm('Удалить заметку?')) deleteNoteMutation.mutate(note.id) }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:opacity-70"
                              style={{ color: 'var(--text-hint)' }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div className="text-[13px] whitespace-pre-wrap mb-2" style={{ color: 'var(--text-primary)' }}>
                            {note.content}
                          </div>
                          <div className="text-[11px]" style={{ color: 'var(--text-hint)' }}>
                            {formatDistanceToNow(new Date(note.created_at), { addSuffix: true, locale: ru })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : !showAddNote ? (
                  <div className="text-center py-8">
                    <MessageSquare size={32} style={{ color: 'var(--text-hint)', margin: '0 auto 8px' }} />
                    <p className="text-[14px]" style={{ color: 'var(--text-hint)' }}>Нет заметок</p>
                  </div>
                ) : null}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-1">
              <Button variant="secondary" onClick={closeDetail}>Закрыть</Button>
              {detailTab === 'data' && (
                <Button onClick={() => setEditMode(true)}><Pencil size={16} className="mr-1.5" />Редактировать</Button>
              )}
            </div>
          </div>
        )}

        {selectedAthlete && editMode && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const cannotClear = (['name', 'date_of_birth', 'phone', 'email', 'parent_phone', 'parent_email', 'address', 'notes', 'medical_clearance', 'weight', 'height'] as const).some(key => selectedAthlete[key] != null && selectedAthlete[key] !== '' && (editForm[key] == null || editForm[key] === ''))
              if (cannotClear) { toast.error('Сервер пока не поддерживает стирание заполненных полей. Укажите новое значение.'); return }
              updateMutation.mutate({ id: selectedAthlete.id, req: editForm })
            }}
            className="flex flex-col gap-4"
          >
            {/* Основные */}
            <SectionTitle>Основные данные</SectionTitle>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Input label="ФИО *" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required autoFocus />
              <Input label="Дата рождения" type="date" value={editForm.date_of_birth || ''} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value || undefined })} />
              <Input aria-label="Телефон спортсмена" label="Телефон спортсмена" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value || undefined })} />
              <Input label="Email спортсмена" type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value || undefined })} />
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>Группа</label>
                {groupsError && <p role="alert" className="text-xs text-red-600">Группы не загрузились. Текущее назначение сохранится.</p>}
                <select
                  aria-label="Группа в карточке спортсмена"
                  value={editForm.group_id ?? (groups.some(g => g.id === selectedAthlete.group_id) ? selectedAthlete.group_id || '' : '')}
                  disabled={!groupsReady}
                  onChange={e => setEditForm({ ...editForm, group_id: e.target.value })}
                  className="h-10 rounded-lg px-3 text-[14px] outline-none "
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                >
                  <option value="">Без группы</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <Input label="Вес (кг)" type="number" value={editForm.weight?.toString() || ''} onChange={(e) => setEditForm({ ...editForm, weight: e.target.value ? parseFloat(e.target.value) : undefined })} />
              <Input label="Рост (см)" type="number" value={editForm.height?.toString() || ''} onChange={(e) => setEditForm({ ...editForm, height: e.target.value ? parseInt(e.target.value) : undefined })} />
              <Input label="Мед. допуск до" type="date" value={editForm.medical_clearance || ''} onChange={(e) => setEditForm({ ...editForm, medical_clearance: e.target.value || undefined })} />
              <Input label="Адрес" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value || undefined })} />
            </div>

            {/* Контакты родителей */}
            <SectionTitle>Контакты родителей</SectionTitle>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Input label="Телефон родителя" value={editForm.parent_phone || ''} onChange={(e) => setEditForm({ ...editForm, parent_phone: e.target.value || undefined })} />
              <Input label="Email родителя" type="email" value={editForm.parent_email || ''} onChange={(e) => setEditForm({ ...editForm, parent_email: e.target.value || undefined })} />
            </div>

            {/* Спортивные данные */}
            <SectionTitle>Спортивные данные</SectionTitle>
            <div className="space-y-2">
              {editForm.sport_data && Object.entries(editForm.sport_data).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-[13px] min-w-[120px] font-medium" style={{ color: 'var(--text-secondary)' }}>{key}</span>
                  <input
                    className="flex-1 h-9 px-3 rounded-lg text-[13px]"
                    style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    value={val}
                    onChange={(e) => setEditForm({ ...editForm, sport_data: { ...editForm.sport_data, [key]: e.target.value } })}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const sd = { ...editForm.sport_data }
                      sd[key] = ''
                      setEditForm({ ...editForm, sport_data: sd })
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:opacity-70"
                    style={{ color: 'var(--danger)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <div className="flex items-end gap-2 pt-1">
                <Input label="Название" value={sportKey} onChange={(e) => setSportKey(e.target.value)} placeholder="напр. Позиция" />
                <Input label="Значение" value={sportVal} onChange={(e) => setSportVal(e.target.value)} placeholder="напр. Вратарь" />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="!h-10"
                  onClick={() => {
                    if (sportKey.trim()) {
                      setEditForm({ ...editForm, sport_data: { ...editForm.sport_data, [sportKey.trim()]: sportVal } })
                      setSportKey('')
                      setSportVal('')
                    }
                  }}
                >
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            {/* Заметки */}
            <SectionTitle>Заметки</SectionTitle>
            <textarea
              value={editForm.notes || ''}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value || undefined })}
              rows={3}
              className="rounded-lg px-3 py-2 text-[14px] outline-none resize-none"
              style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!(editForm.paused ?? false)}
                onChange={(e) => setEditForm({ ...editForm, paused: !e.target.checked })}
                id="edit-active"
                className="w-4 h-4 accent-[var(--accent)]"
              />
              <label htmlFor="edit-active" className="text-[14px]" style={{ color: 'var(--text-primary)' }}>Активен</label>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <Button variant="secondary" type="button" onClick={() => setEditMode(false)}>Отмена</Button>
              <Button type="submit" loading={updateMutation.isPending}>Сохранить</Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[13px] font-semibold uppercase tracking-wider pt-1" style={{ color: 'var(--text-hint)' }}>
      {children}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex gap-2">
      <span className="text-[13px] min-w-[140px]" style={{ color: 'var(--text-hint)' }}>{label}</span>
      <span className="text-[14px]" style={{ color: 'var(--text-primary)' }}>{value || '-'}</span>
    </div>
  )
}
